/** Request body for POST /billing/customer */
export interface CreateBillingCustomerDTO {
  email: string;
  name: string;
}

/** Request body for PUT /billing/customer */
export interface UpdateBillingCustomerDTO {
  email?: string;
  name?: string;
}

export interface CreateSubscriptionDTO {
  promotionCode?: string;
}

/** Response shape for GET /billing/customer */
export interface BillingCustomer {
  email: string;
  name: string;
  billingSystem: string;
  billingId: string;
  paymentMethodId?: string;
  subscription?: {
    id: string;
    status: string;
    createdAt: number;
  };
  lastPaymentInfo?: {
    invoiceId: string;
    status: string;
    amountPaid: number;
    createdAt: number;
  };
  /** "delegated" when billing is covered by the store owner; "self" when the user pays directly. */
  billingMode?: "delegated" | "self";
  /** Store IDs owned directly by the user (not shared/delegated). */
  ownedStoreIds?: string[];
}

/** Returns true when the user's billing is fully delegated to a store owner (no Stripe sub required). */
export function isBillingDelegated(customer: BillingCustomer | null | undefined): boolean {
  return customer?.billingMode === "delegated";
}

export interface PaymentMethodDTO {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  funding: string;
  country: string;
  isDefault: boolean;
}

export interface SubscriptionPlanDTO {
  priceId: string;
  productId: string;
  productName: string;
  unitAmount: number | null;
  currency: string;
  interval: string;
  intervalCount: number;
  tier?: number;
  minClaims?: number;
  maxClaims?: number | null;
}

export interface LatestInvoiceDTO {
  invoiceId: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  status: string;
  hostedInvoiceUrl: string;
  paymentIntentStatus: string | null;
  created: number;
}

/** Active subscription details returned by GET /billing/subscription */
export interface BillingSubscriptionDTO {
  subscriptionId: string;
  status: string;
  productName: string;
  unitAmount: number;
  currency: string;
  priceId: string;
  currentPeriodStart: number;
  nextBillingDate: number;
  cancelAtPeriodEnd: boolean;
  latestInvoice: LatestInvoiceDTO | null;
  clientSecret: string | null;
}

export interface SubscriptionCreateResponse {
  message: string;
  subscription: BillingSubscriptionDTO & { clientSecret: string };
}

/** Response shape for POST /billing/subscription/reverse-cancellation */
export interface ReverseSubscriptionCancellationResponse {
  message: string;
  subscriptionId: string;
}

export interface InvoiceHistoryDTO {
  cursor: string | null;
  limit: number;
  items: BillingInvoiceDTO[];
}

export interface BillingInvoiceDTO {
  invoiceId: string;
  receiptNumber: string | null;
  customerEmail: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  status: string;
  hostedInvoiceUrl: string | null;
  paymentIntentStatus: string | null;
  created: number;
  periodStart: number;
  periodEnd: number;
  subscriptionId: string;
  dueDate: number | null;
}

export interface SubscriptionRetryPaymentResponse {
  message: string;
  paymentIntentId: string;
  clientSecret: string;
  status: string;
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  TRIALING = "trialing",
  INCOMPLETE = "incomplete",
  PAST_DUE = "past_due",
  UNPAID = "unpaid",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  CANCELED = "canceled",
  PAUSED = "paused",
}

export const BILLING_PAYMENT_RETRY_STATUSES = new Set<string>([
  SubscriptionStatus.INCOMPLETE,
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.UNPAID,
]);

export function subscriptionNeedsPaymentRetry(
  subscription: BillingSubscriptionDTO | null | undefined,
  customer?: BillingCustomer | null,
): boolean {
  const status = subscription?.status ?? customer?.subscription?.status;
  return !!status && BILLING_PAYMENT_RETRY_STATUSES.has(status);
}

/** Active billing from GET /billing/customer (bootstrap + guards). */
export function isCustomerBillingActive(
  customer: BillingCustomer | null | undefined,
): boolean {
  if (!customer?.paymentMethodId) {
    return false;
  }

  const status = customer.subscription?.status;
  return (
    !!status &&
    (status === SubscriptionStatus.ACTIVE ||
      status === SubscriptionStatus.TRIALING)
  );
}

const KNOWN_INVOICE_STATUSES = new Set([
  "paid",
  "open",
  "pending",
  "void",
  "draft",
  "uncollectible",
]);

export type InvoiceStatusVariant = "paid" | "pending" | "void" | "draft";

export function getInvoiceStatusTranslationKey(status?: string | null): string {
  const normalized = status?.toLowerCase() ?? "draft";
  return KNOWN_INVOICE_STATUSES.has(normalized)
    ? `billingPage.invoices.detail.statusLabels.${normalized}`
    : "billingPage.invoices.detail.statusLabels.unknown";
}

export function getInvoiceStatusVariant(
  status?: string | null,
): InvoiceStatusVariant {
  const normalized = status?.toLowerCase();
  switch (normalized) {
    case "paid":
      return "paid";
    case "open":
    case "pending":
      return "pending";
    case "void":
    case "uncollectible":
      return "void";
    default:
      return "draft";
  }
}

export function getInvoiceStatusIcon(status?: string | null): string {
  switch (getInvoiceStatusVariant(status)) {
    case "paid":
      return "check_circle";
    case "pending":
      return "schedule";
    case "void":
      return "cancel";
    default:
      return "receipt_long";
  }
}

export interface BillingData {
  customer: BillingCustomer | null;
  paymentMethods: PaymentMethodDTO[];
  subscription: BillingSubscriptionDTO | null;
  invoices: InvoiceHistoryDTO | null;
}

export const BILLING_INVOICE_DEFAULT_PAGE_SIZE = 5;

export interface BillingResolverData {
  billing: BillingData | null;
  plans: SubscriptionPlanDTO[];
}
